import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import axios, { CancelTokenSource } from "axios";

import { CustomInputLabel } from "src/app/components/CustomInput/CustomInput";
import { CustomSwitch } from "src/app/components/CustomSwitch/CustomSwitch";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import CustomTextarea from "src/app/components/CustomTextarea/CustomTextarea";
import CustomSelect from "src/app/components/CustomSelect/CustomSelect";
import { wsAxiosGet } from "src/helpers/wsAxios";
import { API_URLS } from "src/utils/API_URLS";
import { capitalizeFirstLetter } from "src/utils/methods";
import { RootState } from "src/redux/reducers";
import { translateMessage } from "src/app/methods/translateMessage";
import { TaskType } from "src/types";
import {
  AiMetafield,
  MFState,
  SetStateFn,
  SelectedAiMetafield,
} from "../../types";

import "./AiFieldsAssistant.scss";

const createLabelForAIField = (field: AiMetafield): string => {
  let label: string;
  if (field.isTaskMetaField) {
    const taskType = capitalizeFirstLetter(field.metaFieldTaskType) ?? "Task";
    label = `${taskType} > ${field.sourceName}`;
  } else {
    label = `Campaign > ${field.sourceName}`;
  }
  return label;
};

const getAiPoweredMetaFields = async (
  wsWorkspaceUuid: string,
  taskType: TaskType,
  wsProjectUuid: string,
  cancelTokenSource: CancelTokenSource,
): Promise<AiMetafield[]> => {
  let url: string;

  if (wsProjectUuid) {
    url = API_URLS.getLocalAiProjectMetaFieldsToSelectAsInputs
      .replace(":wsProjectUuid:", wsProjectUuid)
      .replace(":taskType:", taskType);
  } else {
    url = API_URLS.getAiProjectMetaFieldsToSelectAsInputs
      .replace(":wsWorkspaceUuid:", wsWorkspaceUuid)
      .replace(":taskType:", taskType);
  }

  try {
    const res = await wsAxiosGet<{ content: AiMetafield[] }>({
      url,
      config: {
        cancelToken: cancelTokenSource.token,
      },
    });
    return res?.content ?? [];
  } catch {
    return [];
  }
};

interface AiFieldsAssistantProps {
  state: MFState;
  setState: SetStateFn;
  projectId: string;
  taskType: TaskType;
}

export default function AiFieldsAssistant({
  state,
  setState,
  projectId,
  taskType,
}: AiFieldsAssistantProps) {
  const [aiMetafields, setAiMetafields] = useState<AiMetafield[]>([]);

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const cancelToken = useRef<CancelTokenSource | null>(null);

  const {
    areAiFieldsEnabled,
    aiPrompt,
    selectedAiMetafields,
    selectedTaskType,
  } = state;

  const intl = useIntl();

  const aiMetafieldsOptions = aiMetafields.map((field) => {
    return {
      label: createLabelForAIField(field),
      value: field.metaFieldUuid,
      isTaskMetaField: field.isTaskMetaField,
    };
  });

  const abort = () => {
    cancelToken?.current?.cancel();
  };

  useEffect(() => {
    if (!activeWorkspaceUuid) return;

    abort();
    cancelToken.current = axios.CancelToken.source();

    getAiPoweredMetaFields(
      activeWorkspaceUuid,
      taskType,
      projectId,
      cancelToken.current,
    ).then(setAiMetafields);
  }, [activeWorkspaceUuid, projectId, taskType]);

  useEffect(() => {
    if (aiMetafields.length === 0 || selectedAiMetafields.length === 0) return;

    const hasLabels = selectedAiMetafields.every((field) => field.label);
    if (hasLabels) return;

    const mappedFields: SelectedAiMetafield[] = [];
    selectedAiMetafields.forEach((selected) => {
      const field = aiMetafields.find(
        (field) => field.metaFieldUuid === selected.value,
      );
      if (!field) return;

      mappedFields.push({
        label: createLabelForAIField(field),
        value: field.metaFieldUuid,
        isTaskMetaField: field.isTaskMetaField,
      });
    });

    setState("selectedAiMetafields", mappedFields);
  }, [aiMetafields, selectedAiMetafields]);

  useEffect(() => {
    return () => {
      abort();
    };
  }, []);

  return (
    <div className="ai-fields-assistant">
      <CustomSwitch
        checked={areAiFieldsEnabled}
        onChange={(e) => setState("areAiFieldsEnabled", e.target.checked)}
        label={
          <IDHFormattedMessage
            id="ws_ai_fields_assistant"
            defaultMessage="AI Fields Assistant"
          />
        }
      />
      {areAiFieldsEnabled && (
        <>
          <div>
            <CustomInputLabel htmlFor="ai-prompt">
              <IDHFormattedMessage
                id="ws_ai_prompt"
                defaultMessage="AI Prompt"
              />
            </CustomInputLabel>
            <CustomTextarea
              value={aiPrompt}
              onChange={(e) => setState("aiPrompt", e.target.value)}
              id="ai-prompt"
              className="ai-fields-assistant__textarea"
            />
          </div>
          <div>
            <CustomInputLabel>
              <IDHFormattedMessage
                id="ws_source_inputs"
                defaultMessage="Source inputs"
              />
            </CustomInputLabel>
            <CustomSelect
              placeholder={`${translateMessage({
                intl,
                id: "ws_select",
                defaultMessage: "Select",
              })}...`}
              isMulti
              commaSeparatedValues
              closeMenuOnSelect={false}
              isSearchable
              value={selectedAiMetafields}
              onChange={(options: SelectedAiMetafield[]) =>
                setState("selectedAiMetafields", options)
              }
              options={aiMetafieldsOptions}
            />
          </div>
        </>
      )}
    </div>
  );
}
